import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Banner, { BannerType } from "components/common/Banner";
import PdfGenerationApi, {
  IPdfGenerationJobResponse,
} from "apis/pdfGeneration/PdfGenerationApi";
import useSelector from "store/useSelector";
import "./PdfReadyPage.scoped.scss";

const AUTO_REFRESH_INTERVAL = 10000; // 10 seconds

type PdfGenerationStatus = "Queued" | "Inprogress" | "Completed" | "Failed";

const PdfReadyPage: React.FC = () => {
  const { jobId } = useParams<{ jobId: string }>();
  const [jobDetails, setJobDetails] = useState<IPdfGenerationJobResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const currentUserEmail = useSelector((store) => store.auth.currentUser.email);

  const fetchJobDetails = useCallback(async () => {
    if (!jobId) return;

    try {
      setError(null);
      const data = await PdfGenerationApi.getPdfGenerationJob(jobId);
      setJobDetails(data);
    } catch (err: any) {
      const message =
        err?.Message || err?.message || "Failed to fetch PDF generation job details.";
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => {
    fetchJobDetails();
  }, [fetchJobDetails]);

  // Auto-refresh for in-progress or queued statuses
  useEffect(() => {
    if (
      jobDetails &&
      (jobDetails.Status === "Inprogress" || jobDetails.Status === "Queued")
    ) {
      intervalRef.current = setInterval(fetchJobDetails, AUTO_REFRESH_INTERVAL);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [jobDetails?.Status, fetchJobDetails]);

  const handleDownload = async () => {
    if (!jobId) return;

    try {
      setDownloading(true);
      setDownloadError(null);
      const { blob, filename } =
        await PdfGenerationApi.getPdfGenerationDownload(jobId);

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = decodeURIComponent(filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      const message =
        err?.Message || err?.message || "Failed to download PDF. Please try again.";
      setDownloadError(message);
    } finally {
      setDownloading(false);
    }
  };

  const getRequestedByDisplay = (): string => {
    if (!jobDetails?.RequestedBy) return "";
    if (
      currentUserEmail &&
      jobDetails.RequestedBy.toLowerCase() === currentUserEmail.toLowerCase()
    ) {
      return "you";
    }
    return jobDetails.RequestedBy;
  };

  const formatDate = (dateStr: string | null | undefined): string => {
    if (!dateStr) return "N/A";
    const date = new Date(dateStr);
    return date.toLocaleString();
  };

  // Loading state
  if (loading) {
    return (
      <div className="pdf-ready-page">
        <h2 className="page-title">PDF Download</h2>
        <div className="loading-container">
          <div className="spinner" />
          <p>Loading PDF generation details...</p>
        </div>
      </div>
    );
  }

  // Error fetching job details (404 or other error)
  if (error || !jobDetails) {
    return (
      <div className="pdf-ready-page">
        <h2 className="page-title">PDF Download</h2>
        <Banner type={BannerType.Error}>
          {error || "PDF generation job not found. The link may be invalid or expired."}
        </Banner>
        <div className="actions">
          <Link to="/" className="btn-link">
            Go to Standard Work
          </Link>
        </div>
      </div>
    );
  }

  const status = jobDetails.Status as PdfGenerationStatus;

  return (
    <div className="pdf-ready-page">
      <h2 className="page-title">PDF Download</h2>

      {/* Status: Completed */}
      {status === "Completed" && (
        <>
          <Banner type={BannerType.Success}>
            Your PDF is ready for download!
          </Banner>
          <div className="pdf-details">
            <div className="detail-row">
              <span className="detail-label">Standard Work:</span>
              <span className="detail-value">{jobDetails.SwTitle || `SW ID: ${jobDetails.SwId}`}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">PDF Type:</span>
              <span className="detail-value">{jobDetails.PdfType}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Generated On:</span>
              <span className="detail-value">{formatDate(jobDetails.PdfGeneratedOn)}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Requested By:</span>
              <span className="detail-value">{getRequestedByDisplay()}</span>
            </div>
          </div>

          {downloadError && (
            <Banner type={BannerType.Error}>
              {downloadError}
            </Banner>
          )}

          <div className="actions">
            <button
              className="btn-primary"
              onClick={handleDownload}
              disabled={downloading}
            >
              {downloading ? "Downloading..." : "Download PDF"}
            </button>
            <Link
              to={`/sw/search`}
              className="btn-link"
            >
              Go back to Standard Work
            </Link>
          </div>
        </>
      )}

      {/* Status: Inprogress */}
      {status === "Inprogress" && (
        <>
          <Banner type={BannerType.Info}>
            Your PDF is being generated, please wait...
          </Banner>
          <div className="pdf-details">
            <div className="detail-row">
              <span className="detail-label">Standard Work:</span>
              <span className="detail-value">{jobDetails.SwTitle || `SW ID: ${jobDetails.SwId}`}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Started At:</span>
              <span className="detail-value">{formatDate(jobDetails.RequestedAt)}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Requested By:</span>
              <span className="detail-value">{getRequestedByDisplay()}</span>
            </div>
          </div>
          <div className="progress-container">
            <div className="spinner" />
            <p>Auto-refreshing every {AUTO_REFRESH_INTERVAL / 1000} seconds...</p>
          </div>
        </>
      )}

      {/* Status: Queued (NotStarted) */}
      {status === "Queued" && (
        <>
          <Banner type={BannerType.Info}>
            PDF generation is queued and will start soon.
          </Banner>
          <div className="pdf-details">
            <div className="detail-row">
              <span className="detail-label">Standard Work:</span>
              <span className="detail-value">{jobDetails.SwTitle || `SW ID: ${jobDetails.SwId}`}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Requested At:</span>
              <span className="detail-value">{formatDate(jobDetails.RequestedAt)}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Requested By:</span>
              <span className="detail-value">{getRequestedByDisplay()}</span>
            </div>
          </div>
          <div className="progress-container">
            <div className="spinner" />
            <p>Auto-refreshing every {AUTO_REFRESH_INTERVAL / 1000} seconds...</p>
          </div>
        </>
      )}

      {/* Status: Failed */}
      {status === "Failed" && (
        <>
          <Banner type={BannerType.Error}>
            PDF generation failed.
          </Banner>
          {jobDetails.Message && (
            <div className="error-details">
              <p><strong>Error Details:</strong> {jobDetails.Message}</p>
            </div>
          )}
          <div className="pdf-details">
            <div className="detail-row">
              <span className="detail-label">Standard Work:</span>
              <span className="detail-value">{jobDetails.SwTitle || `SW ID: ${jobDetails.SwId}`}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Requested At:</span>
              <span className="detail-value">{formatDate(jobDetails.RequestedAt)}</span>
            </div>
          </div>
          <div className="actions">
            <Link
              to={`/sw/search`}
              className="btn-link"
            >
              Go back to Standard Work
            </Link>
          </div>
        </>
      )}
    </div>
  );
};

export default PdfReadyPage;
